/**
 * Pure decisions of the application dialog (AppDialog), which replaced the former alert library (SweetAlert2, removed).
 *
 * AppDialog keeps the call shape the pages already used (`fire(options)` or
 * `fire(title, html, icon)`, a promise of `{ isConfirmed, isDenied, isDismissed, value }`), so
 * that swapping the library did not mean rewriting every confirmation, form and loader by hand.
 * The rules that decide what such a call means live here, without Angular, so they can be tested.
 */

export type AppDialogIcon = 'success' | 'error' | 'warning' | 'info' | 'question';

export type AppDialogInputType =
  | 'text'
  | 'email'
  | 'password'
  | 'number'
  | 'tel'
  | 'url'
  | 'date'
  | 'textarea'
  | 'select'
  | 'radio'
  | 'checkbox';

export enum AppDialogDismissReason {
  cancel = 'cancel',
  backdrop = 'backdrop',
  close = 'close',
  esc = 'esc',
  timer = 'timer',
}

export interface AppDialogCustomClass {
  container?: string;
  popup?: string;
  title?: string;
  htmlContainer?: string;
  actions?: string;
  confirmButton?: string;
  denyButton?: string;
  cancelButton?: string;
  input?: string;
}

export interface AppDialogOptions {
  title?: string;
  titleText?: string;
  text?: string;
  /** Trusted markup written by the page itself (forms, emphasis). Never interpolate user data unescaped. */
  html?: string;
  icon?: AppDialogIcon | string;
  footer?: string;
  width?: string | number;
  background?: string;
  imageUrl?: string;
  imageAlt?: string;
  imageWidth?: string | number;
  imageHeight?: string | number;

  showConfirmButton?: boolean;
  showCancelButton?: boolean;
  showDenyButton?: boolean;
  showCloseButton?: boolean;
  confirmButtonText?: string;
  cancelButtonText?: string;
  denyButtonText?: string;
  confirmButtonColor?: string;
  cancelButtonColor?: string;
  denyButtonColor?: string;
  reverseButtons?: boolean;
  buttonsStyling?: boolean;
  customClass?: AppDialogCustomClass;

  input?: AppDialogInputType | string;
  inputLabel?: string;
  inputPlaceholder?: string;
  inputValue?: any;
  inputOptions?: Record<string, string>;
  inputAttributes?: Record<string, string>;
  inputValidator?: (value: any) => string | null | undefined | void | Promise<string | null | undefined | void>;

  /** Runs on confirm; `false` or a validation message keeps the dialog open, anything else becomes `value`. */
  preConfirm?: (value: any) => any;
  preDeny?: (value: any) => any;
  showLoaderOnConfirm?: boolean;

  allowOutsideClick?: boolean | (() => boolean);
  allowEscapeKey?: boolean | (() => boolean);
  allowEnterKey?: boolean | (() => boolean);

  timer?: number;
  timerProgressBar?: boolean;

  didOpen?: (popup: HTMLElement) => void;
  willClose?: (popup: HTMLElement) => void;
  didClose?: () => void;

  /** Short notice: shown as a snackbar, not as a modal. */
  toast?: boolean;

  /** Legacy alert options with no effect here (position, focusConfirm, heightAuto, target, theme…). */
  [option: string]: any;
}

export interface AppDialogResult<T = any> {
  isConfirmed: boolean;
  isDenied: boolean;
  isDismissed: boolean;
  value?: T;
  dismiss?: AppDialogDismissReason;
}

const ICONS: readonly string[] = ['success', 'error', 'warning', 'info', 'question'];

/**
 * `fire(options)` or the short form `fire(title, html, icon)` — the second argument of the short
 * form is markup, as it was with the former alert library (`'Saved <b>3</b> rows'`).
 */
export function normalizeAppDialogArgs(args: any[]): AppDialogOptions {
  const [first, second, third] = args ?? [];
  if (first !== null && typeof first === 'object' && !Array.isArray(first)) {
    return { ...first };
  }
  const options: AppDialogOptions = {};
  if (first !== undefined && first !== null && first !== '') {
    options.title = String(first);
  }
  if (second !== undefined && second !== null && second !== '') {
    options.html = String(second);
  }
  if (typeof third === 'string' && third !== '') {
    options.icon = third;
  }
  return options;
}

/** The icon to draw, or null for an unknown / missing one (a typo must not draw a broken icon). */
export function appDialogIcon(icon: unknown): AppDialogIcon | null {
  return typeof icon === 'string' && ICONS.includes(icon) ? (icon as AppDialogIcon) : null;
}

/** Dialog width: a number is pixels, a CSS length is kept, nothing gives the historical 32em. */
export function appDialogWidth(width: unknown): string {
  if (typeof width === 'number' && Number.isFinite(width) && width > 0) {
    return `${width}px`;
  }
  if (typeof width === 'string' && width.trim() !== '') {
    return /^\d+(\.\d+)?$/.test(width.trim()) ? `${width.trim()}px` : width.trim();
  }
  return '32em';
}

/** `allowOutsideClick` / `allowEscapeKey`: a boolean, a function evaluated at the moment, or the default. */
export function appDialogAllows(flag: unknown, fallback: boolean): boolean {
  if (typeof flag === 'function') {
    return Boolean(flag());
  }
  if (typeof flag === 'boolean') {
    return flag;
  }
  return fallback;
}

/**
 * Value of a confirmed dialog before preConfirm: the input when there is one (checkbox → 1/0 as
 * the former library did), `true` otherwise.
 */
export function appDialogConfirmValue(inputType: unknown, inputValue: unknown): any {
  if (!inputType) {
    return true;
  }
  if (inputType === 'checkbox') {
    return inputValue ? 1 : 0;
  }
  return inputValue ?? '';
}

/**
 * Outcome of preConfirm / preDeny: `false` keeps the dialog open, `undefined` / `true` keep the
 * default value, anything else replaces it.
 */
export function appDialogPreConfirmOutcome(returned: unknown, fallbackValue: unknown): { stay: boolean; value: any } {
  if (returned === false) {
    return { stay: true, value: fallbackValue };
  }
  if (returned === undefined || returned === true) {
    return { stay: false, value: fallbackValue };
  }
  return { stay: false, value: returned };
}

/** Initial value of the input: select/radio fall back on nothing selected, checkbox on unchecked. */
export function appDialogInitialInputValue(inputType: unknown, inputValue: unknown): any {
  if (inputType === 'checkbox') {
    return Boolean(inputValue);
  }
  if (inputValue === undefined || inputValue === null) {
    return '';
  }
  return inputValue;
}
